import * as Sentry from '@sentry/react';

const IS_SENTRY_ENABLED = !!import.meta.env.VITE_SENTRY_DSN;

// ── Scope Enrichment ─────────────────────────────────────────

export function setSentryUser(user: { id: string; email?: string } | null): void {
  try {
    if (!IS_SENTRY_ENABLED) return;
    Sentry.setUser(user ? { id: user.id, email: user.email } : null);
    Sentry.setTag('email', user?.email ?? undefined);
  } catch (err) {
    console.error('[Logger] setSentryUser failed:', err);
  }
}

export function setSentryEntity(
  entity: { id: string; name?: string; slug?: string | null } | null,
): void {
  try {
    if (!IS_SENTRY_ENABLED) return;
    if (entity) {
      Sentry.setTag('entity_id', entity.id);
      if (entity.name) Sentry.setTag('entity_name', entity.name);
      Sentry.setContext('entity', entity);
    } else {
      Sentry.setTag('entity_id', undefined);
      Sentry.setTag('entity_name', undefined);
      Sentry.setContext('entity', null);
    }
  } catch (err) {
    console.error('[Logger] setSentryEntity failed:', err);
  }
}

export function setSentryOperation(operation: string): void {
  try {
    if (!IS_SENTRY_ENABLED) return;
    Sentry.setTag('operation', operation);
  } catch (err) {
    console.error('[Logger] setSentryOperation failed:', err);
  }
}

// ── Event Capture ────────────────────────────────────────────

export function breadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void {
  try {
    if (!IS_SENTRY_ENABLED) {
      console.log(`[Breadcrumb - ${level}]`, message, data);
      return;
    }

    Sentry.addBreadcrumb({
      message,
      data,
      level,
      timestamp: Date.now() / 1000,
    });
  } catch (err) {
    console.error('[Logger] breadcrumb failed:', err);
  }
}

export function log(message: string, data?: Record<string, unknown>): void {
  try {
    if (!IS_SENTRY_ENABLED) {
      console.error('[Sentry Log]', message, data);
      return;
    }

    if (data) breadcrumb(message, data, 'error');

    Sentry.captureException(new Error(message), {
      level: 'error',
      contexts: { data: data || {} },
    });
  } catch (err) {
    console.error('[Logger] log failed:', err);
  }
}

export function logWarning(message: string, data?: Record<string, unknown>): void {
  try {
    if (!IS_SENTRY_ENABLED) {
      console.warn('[Sentry Warning]', message, data);
      return;
    }

    if (data) breadcrumb(message, data, 'warning');

    Sentry.captureMessage(message, {
      level: 'warning',
      contexts: { data: data || {} },
    });
  } catch (err) {
    console.error('[Logger] logWarning failed:', err);
  }
}

export function logException(error: unknown, data?: Record<string, unknown>): void {
  try {
    // Always log to console regardless of Sentry state
    console.error('[Exception]', error, data);

    if (!IS_SENTRY_ENABLED) {
      return;
    }

    if (data) breadcrumb('Exception occurred', data, 'error');

    let errorToCapture: Error;
    const extraContext: Record<string, unknown> = { ...(data || {}) };

    if (error instanceof Error) {
      errorToCapture = error;
    } else if (error == null) {
      // Handle null/undefined
      errorToCapture = new Error('Unknown error (null or undefined)');
      extraContext.original_value = String(error);
    } else if (typeof error === 'string') {
      errorToCapture = new Error(error);
    } else if (typeof error === 'object') {
      const rec = error as Record<string, unknown>;
      const message =
        (typeof rec.message === 'string' ? rec.message : null) ??
        (typeof rec.error === 'string' ? rec.error : null) ??
        (typeof rec.error_description === 'string' ? rec.error_description : null) ??
        (typeof rec.msg === 'string' ? rec.msg : null) ??
        JSON.stringify(error).slice(0, 500);
      errorToCapture = new Error(message);
      errorToCapture.name = typeof rec.code === 'string' ? rec.code : 'UnknownError';
      Object.assign(extraContext, { error_object: error });
    } else {
      errorToCapture = new Error(String(error));
    }

    Sentry.captureException(errorToCapture, {
      level: 'error',
      contexts: { data: extraContext },
    });
  } catch (err) {
    // Last resort — never let logging crash the app
    console.error('[Logger] logException itself failed:', err);
  }
}
